import { Button, Fab, Switch } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row, Spinner } from "reactstrap";
import { MobiLinkAnimalDto } from "../../generated/tsClient";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import Animal from "./Animal";
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { setAnimalList, setShowSendingPreviewModal, setAnimalListLoadedMessageDate } from "../../store/slices/appSlice";
import BarcodeScannerModal from "../modals/BarcodeScannerModal";
import SendingPreviewModal from "../modals/SendingPreviewModal";
import { toast } from "react-toastify";
import IcomoonReact from "icomoon-react";
import iconSet from "../../icons/selection.json";

function AnimalList(props: any) {
    const { t } = useTranslation('texts');
    const dispatch = useAppDispatch();
    const animalList = useAppSelector((state) => state.app.animalList);
    const selectedCowListOrder = useAppSelector((state) => state.app.selectedCowListOrder);
    const isLoading = useAppSelector((state) => state.app.loading);
    const selectedHerd = useAppSelector((state) => state.app.selectedHerd);
    const animalListLoaded = useAppSelector((state) => state.app.animalListLoaded);
    const animalListLoadedMessageDate = useAppSelector((state) => state.app.animalListLoadedMessageDate);
    const showSettingsModal = useAppSelector((state) => state.app.showSettingsModal);
    const [currentAnimalList, setCurrentAnimalList] = useState<MobiLinkAnimalDto[] | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const [showWithBarcodes, setShowWithBarcodes] = useState(true);
    const [showWithoutBarcodes, setShowWithoutBarcodes] = useState(true);
    const language = useAppSelector((state) => state.app.language);

    useEffect(() => {
        doSearch();
    }, [searchValue, showWithBarcodes, showWithoutBarcodes, animalList, selectedCowListOrder]);

    // NALI-281 show notification if cow list is not updated today and warning message is not shown today.
    useEffect(() => {
        var today = new Date()
        today.setHours(0, 0, 0, 0);

        // Check also that locale is same as language in order to show notification after correct language is loaded.
        if (t("Locale").startsWith(language) && animalListLoaded && new Date(animalListLoaded) < today &&
            (!animalListLoadedMessageDate || new Date(animalListLoadedMessageDate) < today)) {

            var date = new Date(animalListLoaded).toLocaleString(t("Locale"), { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
            var text = t("Notification.NotUpdatedCowList").replace("{0}", date);
            toast.warning(text);

            dispatch(setAnimalListLoadedMessageDate(today));          
        }
    }, [searchValue, showWithBarcodes, showWithoutBarcodes, showSettingsModal]);

    const doSearch = () => {
        var newAnimalList;
        if ((searchValue == null || searchValue.length === 0) && showWithBarcodes && showWithoutBarcodes)
            newAnimalList = animalList;
        else
            newAnimalList = animalList?.filter(a =>
                (
                    a.nameShort?.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
                    || a.earNr?.toString().indexOf(searchValue) !== -1
                    || a.euIdentifier?.substr(10, 4).indexOf(searchValue) !== -1
                )
                &&
                (showWithBarcodes || a.barcode == null)
                &&
                (showWithoutBarcodes || a.barcode != null)
            ) ?? null;

        setCurrentAnimalList(doSort(newAnimalList));
    };

    function compareNullableNumber(a: number | undefined, b: number | undefined): number {
        if (a == null || b == null) {
            if (a == null && b != null)
                return -1;
            if (a != null && b == null)
                return 1;
            return 0;
        }
        else
            return a - b;
    }

    function compareNullableString(a: string | undefined, b: string | undefined): number {
        if (a == null || b == null) {
            if (a == null && b != null)
                return -1;
            if (a != null && b == null)
                return 1;
            return 0;
        }
        else
            return (a > b) ? 1 : ((b > a) ? -1 : 0);
    }

    const doSort = (newAnimalList: MobiLinkAnimalDto[] | null): MobiLinkAnimalDto[] | null => {
        if (newAnimalList == null)
            return null;

        switch (selectedCowListOrder) {
            case 1:
                return [...newAnimalList].sort((a, b) => compareNullableNumber(a.earNr, b.earNr));
            case 2:
                return [...newAnimalList].sort((a, b) => compareNullableString(a.nameShort, b.nameShort));
            case 3:
                return [...newAnimalList].sort((a, b) => compareNullableNumber(a.daysFromInsem, b.daysFromInsem));
        }

        return null;
    }

    const setFocusOnSearch = () => {
        document?.getElementById("animal-search")?.focus();
    }

    const handleBarcodeChange = (euIdentifier: string, barcode: string | undefined) => {
        var barcodeAsNumber: number | undefined = undefined;

        if (barcode != null) {
            if (barcode.length > 11 || !/^\d+$/.test(barcode)) {
                const animal = animalList?.find(a => a.euIdentifier === euIdentifier);
                toast.error(`${t("Errors.InvalidBarcode").replace("{0}", barcode).replace("{1}", `${animal?.earNr} ${animal?.nameShort} ${animal?.euIdentifier}-${animal?.euCheck}`)}`);
                return false;
            }

            // Drop check number from end of barcode.
            barcodeAsNumber = +(barcode.slice(0, -1));

            const duplicate = animalList?.find(a => a.barcode === barcodeAsNumber && a.euIdentifier !== euIdentifier);
            if (duplicate != null) {
                toast.error(`${t("Errors.DublicateBarcode")} ${duplicate.earNr} ${duplicate.nameShort} ${duplicate.euIdentifier}-${duplicate.euCheck}`);
                toast.error(`${t("Errors.CantScanSameBarcode")}`);
                return false;
            }
        }

        if (animalList != null) {
            var newAnimalList: MobiLinkAnimalDto[] = animalList.map((item) => {
                if (item.euIdentifier === euIdentifier) {
                    const updatedItem = structuredClone(item);
                    updatedItem.barcode = barcodeAsNumber;
                    if (barcode == null)
                        updatedItem.pregCheckOrder = false;
                    return updatedItem;
                }
                return item;
            });

            dispatch(setAnimalList(newAnimalList));
            if (newAnimalList.some(a => a.barcode != null)) {
                localStorage.setItem("mobilink_animalList_" + selectedHerd?.idkar, JSON.stringify(newAnimalList));
                localStorage.setItem("mobilink_animalList_loaded_" + selectedHerd?.idkar, JSON.stringify(animalListLoaded));
            }
            else {
                localStorage.removeItem("mobilink_animalList_" + selectedHerd?.idkar);
                localStorage.removeItem("mobilink_animalList_loaded_" + selectedHerd?.idkar);
            }
        }

        return true;
    };

    const handlePregCheckOrderChange = (euIdentifier: string) => {
        if (animalList != null) {
            var newAnimalList: MobiLinkAnimalDto[] = animalList.map((item) => {
                if (item.euIdentifier === euIdentifier) {
                    const updatedItem = structuredClone(item);
                    updatedItem.pregCheckOrder = !item.pregCheckOrder;
                    return updatedItem;
                }
                return item;
            });

            dispatch(setAnimalList(newAnimalList));
            localStorage.setItem("mobilink_animalList_" + selectedHerd?.idkar, JSON.stringify(newAnimalList));
            localStorage.setItem("mobilink_animalList_loaded_" + selectedHerd?.idkar, JSON.stringify(animalListLoaded));
        }
    }

    const handleSendClick = () => {
        dispatch(setShowSendingPreviewModal(true));
    }

    return (
        <div id="animal-list-area">
            <Row>
                <Col xs="4">{t("List.Search")}</Col>
                <Col className="pad-less"><IcomoonReact iconSet={iconSet} icon="barcode" size={20} /> ({animalList?.filter(a => a.barcode != null).length })</Col>
                <Col className="pad-less"><IcomoonReact iconSet={iconSet} icon="no-barcode" size={20} /> ({animalList?.filter(a => a.barcode == null).length })</Col>
                <Col></Col>
            </Row>
            <Row>
                <Col xs="4">
                    <input
                        id="animal-search"
                        type="search"
                        onChange={(e) => setSearchValue(e.target.value) }
                    />
                </Col>
                <Col>
                    <Switch
                        checked={showWithBarcodes }
                        size="small"
                        onChange={(e) => setShowWithBarcodes(e.target.checked) }
                    />
                </Col>
                <Col>
                    <Switch
                        checked={showWithoutBarcodes }
                        size="small"
                        onChange={(e) => setShowWithoutBarcodes(e.target.checked)}
                    />
                </Col>
                <Col>
                    <Button disabled={!animalList?.some(a => a.barcode != null)} className="btn-primary" variant="contained" onClick={handleSendClick }>
                        {isLoading ? (<Spinner
                            style={{ width: "1.5rem", height: "1.5rem" }}
                            color="primary"
                        />) : (<SendOutlinedIcon />)}
                    </Button>
                </Col>
            </Row>
            {currentAnimalList != null && (
            <div id="animal-list">
                {
                    currentAnimalList.map((item) => {
                        return (
                            <Animal
                                animal={item}
                                key={item.euIdentifier}
                                handleBarcodeChange={handleBarcodeChange}
                                handlePregCheckOrderChange={handlePregCheckOrderChange }
                            />
                        )
                    })
                }
            </div>
            )}
            <Fab color="primary" size="medium" onClick={setFocusOnSearch}>
                <SearchOutlinedIcon />
            </Fab>
            <BarcodeScannerModal handleBarcodeChange={handleBarcodeChange} />
            <SendingPreviewModal />
        </div>
    );
}

export default AnimalList;